import { createPortal } from "react-dom";
import { Icon } from "react-migration/components/Icon";
import { DropdownMenu, DropdownMenuSize, useDropdownMenuContext } from "react-migration/components/DropdownMenu";

import Feature from "src/js/stores/user/Feature";
import { Intercom } from "react-migration/lib/util/intercom";
import { ENVIRONMENT } from "src/js/util/environment";
import { refreshUser } from "src/js/util/user";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useAccountQuery } from "react-migration/domains/dashboard/topup/hooks/useAccountQuery";
import { userStore } from "src/js/stores/user/store";
import hasFeature from "src/js/stores/user/actions/hasFeature";

import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { logEvent } from "react-migration/lib/util/logEvent";

import { BalanceUKIcon, BalanceUSIcon, BillingIcon, ChatIcon, ContactIcon, LogoutIcon, PersonIcon } from "../Icons";
import { setShowTopupModal, setDebugModal, openAddOnsStoreModal } from "src/js/stores/navigation/actions";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { Avatar, AvatarSize } from "react-migration/components/Avatar";
import { ReactPortal } from "react";
import { UserColorMenu } from "./UserColorMenu";
import { handleUpgrade, shouldShowUpgrade } from "react-migration/components/UpgradeButton";
import ROUTES from "src/js/router/routes";
import { router } from "src/js/router/react-router";
import clsx from "clsx";

type MenuItem = {
  type: "item" | "item-text" | "item-link" | "separator";
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void | undefined;
  testId?: string;
  details?: React.ReactNode;
  endIcon?: React.ReactNode;
  href?: string;
  extraClasses?: string;
};

export const UserMenu = () => {
  const { t } = useTranslation();
  const { user } = useSnapshot(userStore);
  const { data: accountData } = useAccountQuery();
  const views = accountData?.accountWithViews;
  const isUsUser = hasFeature(Feature.usAccess);
  const hasHometrackV2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const { closeMenu: closeColorMenu } = useDropdownMenuContext();
  const hasProjectsNavBeta = hasBetaFeature(Feature.projectsNavBeta);

  const hasUnlimitedOwnershipClicks = hasFeature(Feature.unlimitedOwnershipClicks);
  const hasUnlimitedPlanningClicks = hasFeature(Feature.unlimitedPlanningClicks);
  const credits = accountData?.accountWithViews?.land_reg_credit_pounds || 0;

  const creditsKeysToDisplayUK = ["ownership_views", "planning_views"];
  const creditsKeysToDisplayUS = ["skip_traces", "included_letter_send"];
  const creditsKeysToDisplay = isUsUser ? creditsKeysToDisplayUS : creditsKeysToDisplayUK;

  const unlimitedMap: Record<string, string> = {
    planning_views: hasUnlimitedPlanningClicks ? "planning_views.unlimited" : "planning_views",
    ownership_views: hasUnlimitedOwnershipClicks ? "ownership_clicks.unlimited" : "ownership_views",
    skip_traces: "skip_traces",
    included_letter_send: "included_letter_send",
  };

  const openTopupModal = () => {
    setShowTopupModal(true);
    logEvent("Top Up Started", { location: "modal" });
  };

  const openDebugModal = () => {
    setDebugModal(true);
    logEvent("Open Debug Modal");
  };

  const openBilling = () => {
    logEvent("Anchor Link Selected", { link: "customer-portal" });
    window.location.replace(`${ENVIRONMENT.ACCOUNTS_SERVICE_PRIVATE_API_URL}/customer-portal`);
  };

  const separator = {
    type: "separator",
  };

  const yourAccountItem = {
    type: "item-text",
    testId: "your-account",
    label: `${user.firstname} ${user.surname}`,
    extraClasses: "mb-1.5",
    details: <span className="text-xs capitalize text-neutral-400">{`${user.plan_name} Plan`}</span>,
    icon: (
      <>
        <DropdownMenu.Provider>
          <DropdownMenu.Trigger align={DropdownMenu.TriggerAlignment["BOTTOM"]} className="select-none cursor-pointer">
            <Avatar
              firstName={user?.firstname ?? ""}
              lastName={user?.surname ?? ""}
              size={AvatarSize.MD}
              className="mr-2"
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="max-w-[160px]">
            <UserColorMenu />
          </DropdownMenu.Content>
        </DropdownMenu.Provider>
      </>
    ),
    endIcon: <PersonIcon />,
  };

  const settingsItem = {
    type: "item",
    label: t("nav.nav.settings"),
    icon: <Icon icon="icon-lt-settings-2-line" />,
    onClick: () => {
      router.push({ name: ROUTES.DASHBOARD });
    },
  };

  const addonsItem = {
    type: "item",
    label: t("nav.nav.addons"),
    icon: <Icon icon="icon-lt-box-3-line" />,
    onClick: () => {
      openAddOnsStoreModal({
        logClickSource: "user-menu",
      });
    },
  };

  const billingItem = {
    type: "item",
    label: t("nav.nav.billing"),
    icon: <Icon icon="icon-lt-receipt-line" />,
    onClick: () => {
      logEvent("Anchor Link Selected", { link: "customer-portal" });
      openBilling();
    },
  };

  const balanceItem = {
    type: "item",
    label: `${t("nav.nav.balance")}: ${credits < 0 ? "-" : ""}${formatToCurrency({
      amount: credits,
      currency: isUsUser ? "USD" : "GBP",
    })}`,
    testId: "balance-element",
    icon: (
      <span className="text-platform-blue-950">
        {isUsUser ? <BalanceUSIcon opacity="1" /> : <BalanceUKIcon opacity="1" />}
      </span>
    ),
    onClick: () => {
      router.push({ name: ROUTES.DASHBOARD_TOPUP });
    },
  };

  const creditsItem = {
    type: "item",
    label: t("nav.nav.credits", {
      type: hasUnlimitedOwnershipClicks ? "Unlimited" : "Limited",
    }),
    icon: <Icon icon="icon-lt-coupon-3-line" />,
    onClick: () => {
      router.push({ name: ROUTES.DASHBOARD_TOPUP });
    },
    details: (
      <div className="flex flex-col w-full gap-none text-neutral-400">
        {views &&
          Object.entries(views)
            .filter(([key]) => creditsKeysToDisplay.includes(key))
            .map(([key, value]) => {
              if ((value.cap || value.cap === 0) && (value.used || value.used === 0)) {
                const displayValue = isUsUser ? value.cap - value.used : value.used;
                return (
                  <span className="text-xs text-neutral-400" key={key}>
                    {t(`nav.nav.credits.${unlimitedMap[key]}`, { value: displayValue, cap: value.cap })}
                  </span>
                );
              }
            })}
      </div>
    ),
  };

  const supportItem = {
    type: "item-text",
    testId: "support-element",
    label: t("nav.nav.support"),
    details: (
      <span className="text-sm capitalize text-neutral-400">
        {hasFeature(Feature.usAccess) ? "calum@land.tech" : "0203 086 7855"}
      </span>
    ),
    endIcon: <ContactIcon opacity="0.6" />,
    onClick: () => {
      Intercom("show");
    },
  };

  const chatItem = {
    type: "item",
    testId: "chat-element",
    label: t("nav.nav.chat"),
    icon: <ChatIcon opacity="1" />,
    onClick: () => {
      Intercom("show");
    },
  };

  const debugItem =
    ENVIRONMENT.APP_ENV === "local" || hasBetaFeature(Feature.debugMenu)
      ? [
          {
            type: "item",
            testId: "debug-element",
            label: t("nav.nav.debug"),
            icon: <Icon icon="icon-lt-alert-line" extraClasses="text-platform-blue-950" />,
            onClick: () => {
              openDebugModal();
            },
          },
        ]
      : [];

  const logoutItem = {
    type: "item-link",
    testId: "logout-element",
    label: t("nav.nav.logout"),
    icon: <LogoutIcon opacity="1" />,
    href: ENVIRONMENT.LOGOUT_URL,
  };

  const refreshUserItem =
    ENVIRONMENT.APP_ENV === "local"
      ? [
          {
            type: "item",
            testId: "refresh-user",
            label: "Refresh User",
            icon: <PersonIcon opacity="1" />,
            onClick: () => {
              refreshUser();
            },
          },
        ]
      : [];

  const menuConfig = [
    yourAccountItem,
    billingItem,
    isUsUser ? null : balanceItem,
    creditsItem,

    separator,

    isUsUser ? null : addonsItem,
    settingsItem,

    separator,

    supportItem,
    chatItem,
    ...debugItem,

    separator,

    logoutItem,
    ...refreshUserItem,
  ].filter(Boolean) as MenuItem[];

  return (
    <DropdownMenu.Provider size={DropdownMenuSize.AUTO}>
      <DropdownMenu.Trigger
        data-testid="usermenu-trigger-element"
        align={DropdownMenu.TriggerAlignment["BOTTOM"]}
        className="select-none cursor-pointer"
      >
        <Avatar
          firstName={user?.firstname ?? ""}
          lastName={user?.surname ?? ""}
          size={AvatarSize.MD}
          backgroundExtraClass="hover:brightness-90"
          className="relative"
        />
      </DropdownMenu.Trigger>
      {
        createPortal(
          <DropdownMenu.Content className={clsx("min-w-[242px]", hasProjectsNavBeta && "py-1")}>
            {hasProjectsNavBeta ? (
              menuConfig.map(({ label, icon, onClick, testId, details, type, endIcon, href, extraClasses }, index) => {
                switch (type) {
                  case "item":
                    return (
                      <DropdownMenu.Item
                        key={label}
                        onClick={onClick}
                        data-testid={testId}
                        className="flex items-start gap-2 text-blue-grey-950 py-1! mx-1.5 my-0.5"
                      >
                        <span className="flex items-center text-platform-blue-950">{icon}</span>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm">{label}</span>
                          {details && details}
                        </div>
                      </DropdownMenu.Item>
                    );
                  case "item-text":
                    return (
                      <DropdownMenu.ItemText key={label} className={clsx("py-1! mx-1.5 my-0.5", extraClasses)}>
                        <div data-testid={testId} className="flex flex-row items-center flex-1">
                          {icon}
                          <div className="flex grow">
                            <div className="flex flex-col">
                              <span className={clsx("text-sm text-blue-grey-950")}>{label}</span>
                              {details}
                            </div>
                          </div>
                          <div className="flex-none">{endIcon}</div>
                        </div>
                      </DropdownMenu.ItemText>
                    );
                  case "item-link":
                    return (
                      <DropdownMenu.ItemLink
                        key={label}
                        href={href}
                        data-testid={testId}
                        className="flex items-center gap-2 py-1! mx-1.5 my-0.5"
                      >
                        {icon}
                        <span className="text-sm">{label}</span>
                      </DropdownMenu.ItemLink>
                    );
                  default:
                    return <DropdownMenu.Separator className="bg-neutral-200 my-3 mx-2" key={`separator-${index}`} />;
                }
              })
            ) : (
              <>
                <DropdownMenu.ItemText>
                  <div data-testid="your-account" className="flex flex-row items-center flex-1">
                    <DropdownMenu.Provider onMenuClosed={() => closeColorMenu()}>
                      <DropdownMenu.Trigger align={DropdownMenu.TriggerAlignment["BOTTOM"]}>
                        <Avatar
                          firstName={user?.firstname ?? ""}
                          lastName={user?.surname ?? ""}
                          size={AvatarSize.MD}
                          className="mr-2"
                        />
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content className="max-w-[160px]">
                        <UserColorMenu />
                      </DropdownMenu.Content>
                    </DropdownMenu.Provider>
                    <div className="flex grow">
                      <div className="flex flex-col">
                        <span className="text-sm">{`${user.firstname} ${user.surname}`}</span>
                        <span className="text-xs capitalize text-neutral-400">{`${user.plan_name} Plan`}</span>
                      </div>
                    </div>
                    <div className="flex-none">
                      <PersonIcon />
                    </div>
                  </div>
                </DropdownMenu.ItemText>
                {shouldShowUpgrade("UserMenu") && (
                  <DropdownMenu.Item
                    onClick={() => handleUpgrade({ source: "UserMenu" })}
                    data-testid="logout-element"
                    className="flex items-center gap-2"
                  >
                    <Icon icon="icon-lt-arrow-up-circle-line" extraClasses="opacity-60" />
                    <span className="text-sm">{t("nav.nav.upgrade")}</span>
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  onClick={() => openBilling()}
                  data-testid="logout-element"
                  className="flex items-center gap-2"
                >
                  <BillingIcon />
                  <span className="text-sm">{t("nav.nav.billing")}</span>
                </DropdownMenu.Item>
                {!hasFeature(Feature.usAccess) && (
                  <DropdownMenu.Item
                    data-testid="logout-element"
                    className="flex items-center gap-2"
                    disabled={!hasFeature(Feature.documentPurchase)}
                    onClick={() => openTopupModal()}
                  >
                    {hasFeature(Feature.usAccess) ? <BalanceUSIcon /> : <BalanceUKIcon />}
                    <span className="text-sm">{t("nav.nav.balance")}</span>
                    <span className="text-sm">
                      {(accountData?.accountWithViews?.land_reg_credit_pounds || 0) < 0 && "-"}
                      {`${formatToCurrency({
                        amount: accountData?.accountWithViews.land_reg_credit_pounds || 0,
                        currency: hasFeature(Feature.usAccess) ? "USD" : "GBP",
                      })}`}
                    </span>
                  </DropdownMenu.Item>
                )}
                {!isUsUser && hasHometrackV2 && (
                  <DropdownMenu.Item
                    onClick={() =>
                      openAddOnsStoreModal({
                        logClickSource: "user-menu",
                      })
                    }
                    data-testid="addons-store"
                    className="flex items-center gap-2"
                  >
                    <Icon icon="icon-lt-box-3-line" extraClasses="opacity-60" />
                    <span className="text-sm">{t("nav.nav.addons")}</span>
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator className="bg-neutral-200 mx-3" />
                <DropdownMenu.ItemText>
                  <div data-testid="your-account" className="flex flex-row items-center flex-1">
                    <div className="flex grow">
                      <div className="flex flex-col">
                        <span className="text-sm">{t("nav.nav.support")}</span>
                        <span className="text-sm capitalize text-neutral-400">
                          {hasFeature(Feature.usAccess) ? "calum@land.tech" : "0203 086 7855"}
                        </span>
                      </div>
                    </div>
                    <div className="flex-none">
                      <ContactIcon />
                    </div>
                  </div>
                </DropdownMenu.ItemText>
                <DropdownMenu.Item
                  data-testid="logout-element"
                  className="flex items-center gap-2"
                  onClick={() => Intercom("show")}
                >
                  <ChatIcon />
                  <span className="text-sm">{t("nav.nav.chat")}</span>
                </DropdownMenu.Item>
                {(ENVIRONMENT.APP_ENV === "local" || hasBetaFeature(Feature.debugMenu)) && (
                  <DropdownMenu.Item
                    data-testid="clear-cache"
                    className="flex items-center gap-2"
                    onClick={() => openDebugModal()}
                  >
                    <Icon icon="icon-lt-alert-line" extraClasses="opacity-60" />
                    <span className="text-sm">{t("nav.nav.debug")}</span>
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator />
                <DropdownMenu.ItemLink
                  href={ENVIRONMENT.LOGOUT_URL}
                  data-testid="logout-element"
                  className="flex items-center gap-2"
                >
                  <LogoutIcon />
                  <span className="text-sm">{t("nav.nav.logout")}</span>
                </DropdownMenu.ItemLink>
                {ENVIRONMENT.APP_ENV === "local" && (
                  <DropdownMenu.ItemLink
                    data-testid="refresh-user"
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => refreshUser()}
                  >
                    <PersonIcon />
                    <span className="text-sm">Refresh User</span>
                  </DropdownMenu.ItemLink>
                )}
              </>
            )}
          </DropdownMenu.Content>,
          document.body
        ) as ReactPortal
      }
    </DropdownMenu.Provider>
  );
};
